import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, UserPlus, Shield, Trash2, Loader2, ChevronDown, Search, Building2, Pencil, Check, X, Key, Copy, Eye, EyeOff, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  { value: 'admin', label: 'Admin', color: 'bg-red-50 text-red-700', desc: 'Full access + user management' },
  { value: 'analyst', label: 'Analyst', color: 'bg-blue-50 text-blue-700', desc: 'Upload, classify, correct, export' },
  { value: 'viewer', label: 'Viewer', color: 'bg-slate-100 text-slate-600', desc: 'Read-only dashboards & reports' },
];

const RoleBadge = ({ role }) => {
  const r = ROLES.find(x => x.value === role) || ROLES[2];
  return <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${r.color}`}>{r.label}</span>;
};

const InviteForm = ({ token, onDone }) => {
  const [form, setForm] = useState({ email: '', full_name: '', organization_name: '', password: '', role: 'analyst' });
  const [autoPassword, setAutoPassword] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleInvite = async () => {
    if (!form.email || !form.full_name) {
      toast.error('Email and Full Name are required');
      return;
    }
    if (!autoPassword && !form.password) {
      toast.error('Temporary password is required when auto-generate is off');
      return;
    }
    setSaving(true);
    try {
      // When auto-generate is on, omit `password` so backend generates one
      // and emails the invite. When off, send the admin-typed password.
      const payload = autoPassword
        ? { email: form.email, full_name: form.full_name,
            organization_name: form.organization_name, role: form.role }
        : form;
      const res = await axios.post(`${API}/admin/users/invite`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data || {};
      if (data.temp_password_fallback) {
        // Email delivery failed — surface the temp password so the admin can
        // share it manually. Long duration so they have time to copy it.
        toast.warning(
          `Invited ${form.email} but email delivery failed. ` +
          `Temp password: ${data.temp_password_fallback}`,
          { duration: 30000 }
        );
      } else if (data.email_delivered) {
        toast.success(`Invite email sent to ${form.email} as ${form.role}`);
      } else {
        // Email disabled / no fallback (admin typed the password themselves)
        toast.success(`User ${form.email} invited as ${form.role}. Share their sign-in details securely.`);
      }
      setForm({ email: '', full_name: '', organization_name: '', password: '', role: 'analyst' });
      setAutoPassword(true);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite user');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="invite-form">
      <h3 className="text-sm font-semibold text-[#0F172A]">Invite New User</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input value={form.full_name} onChange={e => set('full_name', e.target.value)}
          placeholder="Full Name" data-testid="invite-name"
          className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <input value={form.organization_name} onChange={e => set('organization_name', e.target.value)}
          placeholder="Organization Name (optional)" data-testid="invite-org-name"
          className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <input value={form.email} onChange={e => set('email', e.target.value)} type="email"
          placeholder="Email address" data-testid="invite-email"
          className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <select value={form.role} onChange={e => set('role', e.target.value)} data-testid="invite-role"
          className="px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} - {r.desc}</option>)}
        </select>
      </div>

      {/* Password mode toggle */}
      <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-2">
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="invite-auto-password-toggle">
          <input type="checkbox" checked={autoPassword}
            onChange={e => setAutoPassword(e.target.checked)}
            className="mt-0.5 accent-[#0891b2]"
            data-testid="invite-auto-password-checkbox" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-slate-800">
              Auto-generate temporary password &amp; email invite
              <span className="ml-1.5 text-[10px] font-normal text-emerald-600 uppercase tracking-wide">Recommended</span>
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              A 14-character password is generated and emailed to the user with sign-in instructions.
              They&apos;ll be forced to change it on first login. If email delivery fails, you&apos;ll see the temp password here.
            </p>
          </div>
        </label>
        {!autoPassword && (
          <div className="pl-6 pt-1">
            <input value={form.password} onChange={e => set('password', e.target.value)} type="text"
              placeholder="Temporary password (min 8 characters)" data-testid="invite-password"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-[11px] text-slate-500 mt-1.5">
              The user will still be required to change this on first login. Share it via a secure channel.
            </p>
          </div>
        )}
      </div>

      <Button onClick={handleInvite} disabled={saving} data-testid="invite-submit-btn"
        className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1.5">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
        {autoPassword ? 'Invite & Email User' : 'Invite User'}
      </Button>
    </div>
  );
};

const ApiKeysSection = ({ token }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('SpendLens Integration');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showKey, setShowKey] = useState(false);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api-keys`, { headers: { Authorization: `Bearer ${token}` } });
      setKeys(res.data.keys || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api-keys`, { label: newLabel }, { headers: { Authorization: `Bearer ${token}` } });
      setNewKey(res.data.api_key);
      setShowKey(true);
      toast.success('API key created. Copy it now — it won\'t be shown again.');
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    } finally { setCreating(false); }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Revoke this API key? SpendLens will lose access.')) return;
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('API key revoked');
      fetchKeys();
    } catch (err) {
      toast.error('Failed to revoke key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="api-keys-section">
      <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key size={18} className="text-slate-600" />
          <h2 className="text-base font-semibold text-slate-800">External API Keys</h2>
          <span className="text-xs text-slate-400">for SpendLens and other integrations</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Create new key */}
        <div className="flex items-center gap-2">
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Key label (e.g., SpendLens Production)"
            className="flex-1 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="api-key-label-input"
          />
          <Button onClick={handleCreate} disabled={creating || !newLabel.trim()} size="sm"
            className="bg-[#0F172A] hover:bg-slate-800 text-white gap-1" data-testid="create-api-key-btn">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
            Generate Key
          </Button>
        </div>

        {/* Newly created key display */}
        {newKey && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="new-key-display">
            <p className="text-xs font-semibold text-emerald-800 mb-2">New API Key (copy now — won't be shown again):</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border border-emerald-200 rounded px-3 py-2 text-xs font-mono text-emerald-900 select-all">
                {showKey ? newKey : '•'.repeat(40)}
              </code>
              <button onClick={() => setShowKey(!showKey)} className="p-2 hover:bg-emerald-100 rounded" data-testid="toggle-key-visibility">
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={() => copyToClipboard(newKey)} className="p-2 hover:bg-emerald-100 rounded" data-testid="copy-key-btn">
                <Copy size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Existing keys */}
        {loading ? (
          <div className="py-6 text-center"><Loader2 size={20} className="animate-spin mx-auto text-slate-300" /></div>
        ) : keys.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">No API keys yet. Generate one to enable SpendLens integration.</p>
        ) : (
          <table className="w-full" data-testid="api-keys-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Label</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Key</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Created</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Last Used</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {keys.map(k => (
                <tr key={k.id} className="hover:bg-slate-50" data-testid={`api-key-row-${k.id}`}>
                  <td className="px-3 py-2 text-sm text-slate-700 font-medium">{k.label}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-500">{k.key_prefix}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${k.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                      {k.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{k.created_at ? new Date(k.created_at).toLocaleDateString() : '-'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{k.last_used ? new Date(k.last_used).toLocaleDateString() : 'Never'}</td>
                  <td className="px-3 py-2">
                    {k.is_active && (
                      <button onClick={() => handleRevoke(k.id)} className="text-xs text-red-500 hover:text-red-700 font-medium" data-testid={`revoke-key-${k.id}`}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Usage Guide */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-2" data-testid="api-usage-guide">
          <p className="text-xs font-semibold text-slate-600">SpendLens Integration Guide</p>
          <div className="text-xs text-slate-500 space-y-1.5 font-mono">
            <p className="font-sans text-slate-600 font-medium">1. List available sessions:</p>
            <code className="block bg-white border border-slate-200 rounded px-2 py-1 break-all">
              GET {baseUrl}/api/external/sessions -H "X-API-Key: dr_..."
            </code>
            <p className="font-sans text-slate-600 font-medium mt-2">2. Pull classified data (paginated):</p>
            <code className="block bg-white border border-slate-200 rounded px-2 py-1 break-all">
              GET {baseUrl}/api/external/classified-data?upload_id=SESSION_ID&page=1&per_page=100 -H "X-API-Key: dr_..."
            </code>
            <p className="font-sans text-slate-500 mt-1">Response fields: id, supplier, description, amount, currency, date, category_l1-l4, payment_terms, extra columns, taxonomy_type, is_split_transaction</p>
          </div>
        </div>
      </div>
    </div>
  );
};


export const AdminPanel = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [search, setSearch] = useState('');
  const [editingRole, setEditingRole] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgDraft, setOrgDraft] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data.users || []);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
      else toast.error('Failed to load users');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('Role updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally { setEditingRole(null); }
  };

  const handleOrgSave = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/organization`, { organization_name: orgDraft },
        { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, organization_name: orgDraft } : u));
      toast.success('Organization name updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update organization');
    } finally { setEditingOrg(null); }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const [resending, setResending] = useState({});  // {userId: true}
  const handleResend = async (user) => {
    if (!window.confirm(
      `Reset ${user.full_name || user.email}'s password and email them a new invite? ` +
      `Their current password will stop working immediately.`)) return;
    setResending(prev => ({ ...prev, [user.id]: true }));
    try {
      const res = await axios.post(`${API}/admin/users/${user.id}/resend-invite`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data || {};
      if (data.temp_password_fallback) {
        toast.warning(
          `Password reset for ${user.email} but email delivery failed. ` +
          `Temp password: ${data.temp_password_fallback}`,
          { duration: 30000 }
        );
      } else if (data.email_delivered) {
        toast.success(`New invite email sent to ${user.email}`);
      } else {
        toast.success(`Password reset for ${user.email}. Share the new sign-in details securely.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setResending(prev => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
    }
  };

  const filtered = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.organization_name?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    admin: users.filter(u => u.role === 'admin').length,
    analyst: users.filter(u => u.role === 'analyst').length,
    viewer: users.filter(u => u.role === 'viewer').length,
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 size={32} className="animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">User Management</h1>
          <p className="text-slate-600">Manage team members and their access roles</p>
        </div>
        <Button onClick={() => setShowInvite(!showInvite)}
          className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1.5" data-testid="toggle-invite-btn">
          <UserPlus size={16} /> {showInvite ? 'Hide Form' : 'Invite User'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Admins', count: stats.admin, color: 'border-red-200 bg-red-50' },
          { label: 'Analysts', count: stats.analyst, color: 'border-blue-200 bg-blue-50' },
          { label: 'Viewers', count: stats.viewer, color: 'border-slate-200 bg-slate-50' },
        ].map(s => (
          <div key={s.label} className={`p-4 border rounded-sm ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>
            <p className="text-2xl font-bold text-[#0F172A]">{s.count}</p>
            <p className="text-xs text-slate-600">{s.label}</p>
          </div>
        ))}
      </div>

      {showInvite && <InviteForm token={token} onDone={() => { setShowInvite(false); fetchUsers(); }} />}

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search users..." data-testid="user-search"
          className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      {/* User Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="users-table">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">User</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Organization</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Role</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Joined</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(user => (
              <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                data-testid={`user-row-${user.id}`}>
                <td className="px-4 py-3">
                  <p className="text-sm font-medium text-slate-900">{user.full_name}</p>
                  <p className="text-xs text-slate-500">{user.email}</p>
                </td>
                <td className="px-4 py-3" data-testid={`org-cell-${user.id}`}>
                  {editingOrg === user.id ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        value={orgDraft}
                        onChange={e => setOrgDraft(e.target.value)}
                        placeholder="Organization name"
                        data-testid={`org-input-${user.id}`}
                        className="px-2 py-1 border border-slate-200 rounded-sm text-xs w-36 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') handleOrgSave(user.id); if (e.key === 'Escape') setEditingOrg(null); }}
                      />
                      <button onClick={() => handleOrgSave(user.id)} className="p-0.5 text-green-600 hover:bg-green-50 rounded"
                        data-testid={`org-save-${user.id}`}><Check size={14} /></button>
                      <button onClick={() => setEditingOrg(null)} className="p-0.5 text-slate-400 hover:bg-slate-100 rounded"
                        data-testid={`org-cancel-${user.id}`}><X size={14} /></button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingOrg(user.id); setOrgDraft(user.organization_name || ''); }}
                      className="flex items-center gap-1.5 group text-left"
                      data-testid={`org-edit-btn-${user.id}`}
                    >
                      {user.organization_name ? (
                        <span className="text-xs font-medium text-slate-700 flex items-center gap-1">
                          <Building2 size={12} className="text-slate-400" />
                          {user.organization_name}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">Not set</span>
                      )}
                      <Pencil size={11} className="text-slate-300 group-hover:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingRole === user.id ? (
                    <div className="flex items-center gap-2">
                      <select defaultValue={user.role}
                        onChange={e => handleRoleChange(user.id, e.target.value)}
                        className="px-2 py-1 border border-slate-200 rounded-sm text-xs bg-white focus:ring-2 focus:ring-blue-500"
                        data-testid={`role-select-${user.id}`}>
                        {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                      <button onClick={() => setEditingRole(null)} className="text-xs text-slate-400">cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setEditingRole(user.id)} className="flex items-center gap-1 group"
                      data-testid={`role-badge-${user.id}`}>
                      <RoleBadge role={user.role} />
                      <ChevronDown size={12} className="text-slate-300 group-hover:text-slate-500" />
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <button onClick={() => handleResend(user)}
                      disabled={!!resending[user.id] || user.role === 'super_user'}
                      title={user.role === 'super_user'
                        ? 'Cannot resend invite to a Super User'
                        : 'Reset password & re-email invite'}
                      className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400"
                      data-testid={`resend-invite-${user.id}`}>
                      {resending[user.id] ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                    </button>
                    <button onClick={() => handleDelete(user.id, user.email)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                      data-testid={`delete-user-${user.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">No users found</div>
        )}
      </div>

      {/* API Keys for External Integrations */}
      <ApiKeysSection token={token} />

      {/* Role Reference */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
        <p className="text-xs font-semibold text-slate-600 mb-2">Role Permissions Reference</p>
        <div className="grid grid-cols-3 gap-3">
          {ROLES.map(r => (
            <div key={r.value} className="text-xs text-slate-600">
              <RoleBadge role={r.value} /> <span className="ml-1.5">{r.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
